"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "WrappedTarget", {
    enumerable: true,
    get: function() {
        return WrappedTarget;
    }
});
const _bufferTransform = require("./bufferTransform.js");
const _getLageOutputCacheLocation = require("./getLageOutputCacheLocation.js");
const _logger = require("@lage-run/logger");
const _fs = /*#__PURE__*/ _interop_require_default(require("fs"));
const _path = /*#__PURE__*/ _interop_require_default(require("path"));
const _promises = require("fs/promises");
const _targetgraph = require("@lage-run/target-graph");
function _check_private_redeclaration(obj, privateCollection) {
    if (privateCollection.has(obj)) {
        throw new TypeError("Cannot initialize the same private elements twice on an object");
    }
}
function _class_apply_descriptor_get(receiver, descriptor) {
    if (descriptor.get) {
        return descriptor.get.call(receiver);
    }
    return descriptor.value;
}
function _class_apply_descriptor_set(receiver, descriptor, value) {
    if (descriptor.set) {
        descriptor.set.call(receiver, value);
    } else {
        if (!descriptor.writable) {
            throw new TypeError("attempted to set read only private field");
        }
        descriptor.value = value;
    }
}
function _class_extract_field_descriptor(receiver, privateMap, action) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to " + action + " private field on non-instance");
    }
    return privateMap.get(receiver);
}
function _class_private_field_get(receiver, privateMap) {
    var descriptor = _class_extract_field_descriptor(receiver, privateMap, "get");
    return _class_apply_descriptor_get(receiver, descriptor);
}
function _class_private_field_init(obj, privateMap, value) {
    _check_private_redeclaration(obj, privateMap);
    privateMap.set(obj, value);
}
function _class_private_field_set(receiver, privateMap, value) {
    var descriptor = _class_extract_field_descriptor(receiver, privateMap, "set");
    _class_apply_descriptor_set(receiver, descriptor, value);
    return value;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
var _status = /*#__PURE__*/ new WeakMap(), _result = /*#__PURE__*/ new WeakMap();
class WrappedTarget {
    get result() {
        return _class_private_field_get(this, _result);
    }
    get status() {
        return _class_private_field_get(this, _status);
    }
    get abortController() {
        return this.options.abortController;
    }
    set abortController(abortController) {
        this.options.abortController = abortController;
    }
    get successful() {
        return _class_private_field_get(this, _status) === "skipped" || _class_private_field_get(this, _status) === "success";
    }
    get waiting() {
        return _class_private_field_get(this, _status) === "pending" || _class_private_field_get(this, _status) === "queued";
    }
    onQueued() {
        _class_private_field_set(this, _status, "queued");
        this.queueTime = process.hrtime();
        this.options.logger.info("", {
            target: this.target,
            status: "queued"
        });
    }
    onAbort() {
        _class_private_field_set(this, _status, "aborted");
        this.options.logger.info("", {
            target: this.target,
            status: "aborted",
            threadId: this.threadId
        });
    }
    onStart(threadId) {
        if (this.status !== "running") {
            this.threadId = threadId;
            _class_private_field_set(this, _status, "running");
            this.startTime = process.hrtime();
            this.options.logger.info("", {
                target: this.target,
                status: "running",
                threadId
            });
        }
    }
    onComplete() {
        _class_private_field_set(this, _status, "success");
        this.options.logger.info("", {
            target: this.target,
            status: "success",
            duration: this.duration,
            threadId: this.threadId
        });
    }
    onFail() {
        _class_private_field_set(this, _status, "failed");
        this.options.logger.info("", {
            target: this.target,
            status: "failed",
            duration: this.duration,
            threadId: this.threadId
        });
        if (!this.options.continueOnError && this.options.abortController) {
            this.options.abortController.abort();
        }
    }
    onSkipped(hash) {
        _class_private_field_set(this, _status, "skipped");
        if (hash) {
            this.options.logger.info("", {
                target: this.target,
                status: "skipped",
                duration: this.duration,
                hash,
                threadId: this.threadId
            });
        }
    }
    async run() {
        const { target, logger, shouldCache, abortController, root } = this.options;
        const abortSignal = abortController.signal;
        if (abortSignal.aborted) {
            this.onStart(0);
            this.onAbort();
            return;
        }
        try {
            _class_private_field_set(this, _result, await this.runInPool());
            const cacheEnabled = target.cache && shouldCache && _class_private_field_get(this, _result).hash;
            // Save output if cache is enabled & cache is hit
            if (!_class_private_field_get(this, _result).skipped && cacheEnabled) {
                const outputLocation = (0, _getLageOutputCacheLocation.getLageOutputCacheLocation)(root, _class_private_field_get(this, _result).hash);
                const outputPath = _path.default.dirname(outputLocation);
                await (0, _promises.mkdir)(outputPath, {
                    recursive: true
                });
                const output = `${_class_private_field_get(this, _result).stdoutBuffer}\n${_class_private_field_get(this, _result).stderrBuffer}`;
                await (0, _promises.writeFile)(outputLocation, output);
                logger.verbose(`>> Saved cache - ${_class_private_field_get(this, _result).hash}`, {
                    target
                });
            }
            if (_class_private_field_get(this, _result).skipped) {
                const { hash } = _class_private_field_get(this, _result);
                const cachedOutputFile = (0, _getLageOutputCacheLocation.getLageOutputCacheLocation)(root, hash ?? "");
                const shouldShowCachedOutput = _fs.default.existsSync(cachedOutputFile);
                if (shouldShowCachedOutput) {
                    const cachedOutput = _fs.default.readFileSync(cachedOutputFile, "utf8");
                    logger.verbose(">> Replaying cached output", {
                        target
                    });
                    logger.verbose(cachedOutput.trim(), {
                        target
                    });
                }
                this.onSkipped(hash);
            } else {
                this.onComplete();
            }
            return _class_private_field_get(this, _result);
        } catch (e) {
            if (e instanceof Error) {
                logger.error(String(e), {
                    target
                });
            }
            if (abortSignal.aborted) {
                this.onAbort();
            } else {
                this.onFail();
            }
            throw e;
        }
    }
    async runInPool() {
        const { target, logger, abortController, pool } = this.options;
        const abortSignal = abortController.signal;
        let releaseStdout;
        let releaseStderr;
        const bufferStdout = (0, _bufferTransform.bufferTransform)();
        const bufferStderr = (0, _bufferTransform.bufferTransform)();
        let msgHandler;
        const result = await pool.exec({
            target
        }, target.weight ?? 1, (worker, stdout, stderr)=>{
            const postMessage = worker.postMessage.bind(worker);
            msgHandler = (data)=>{
                if (data.type === "log") {
                    logger.log(data.level, data.msg, {
                        target,
                        threadId: worker.threadId
                    });
                } else if (data.type === "hash") {
                    this.options.hasher.hash(target).then((hash)=>{
                        worker.postMessage({
                            type: "hash",
                            hash
                        });
                    });
                } else if (this.options.onMessage) {
                    this.options.onMessage(data, postMessage);
                }
            };
            worker.on("message", msgHandler);
            const threadId = worker.threadId;
            this.onStart(threadId);
            stdout.pipe(bufferStdout.transform);
            stderr.pipe(bufferStderr.transform);
            const releaseStdoutStream = logger.stream(_logger.LogLevel.verbose, stdout, {
                target,
                threadId
            });
            releaseStdout = ()=>{
                releaseStdoutStream();
                stdout.unpipe(bufferStdout.transform);
            };
            const releaseStderrStream = logger.stream(_logger.LogLevel.verbose, stderr, {
                target,
                threadId
            });
            releaseStderr = ()=>{
                releaseStderrStream();
                stderr.unpipe(bufferStderr.transform);
            };
        }, (worker)=>{
            worker.off("message", msgHandler);
            this.duration = process.hrtime(this.startTime);
            releaseStdout();
            releaseStderr();
        }, abortSignal, target.priority);
        return {
            stdoutBuffer: bufferStdout.buffer,
            stderrBuffer: bufferStderr.buffer,
            skipped: result?.skipped,
            hash: result?.hash,
            value: result?.value,
            id: result?.id
        };
    }
    /**
   * A JSON representation of this wrapped target, suitable for serialization in tests.
   *
   * Skips the unpredictable properties of the wrapped target like the startTime and duration.
   *
   * @returns
   */ toJSON() {
        return {
            target: this.target.id,
            status: this.status
        };
    }
    /**
   * Reset the state of this wrapped target.
   */ reset() {
        _class_private_field_set(this, _result, undefined);
        _class_private_field_set(this, _status, "pending");
    }
    constructor(options){
        _define_property(this, "options", void 0);
        _class_private_field_init(this, _status, {
            writable: true,
            value: void 0
        });
        _class_private_field_init(this, _result, {
            writable: true,
            value: void 0
        });
        _define_property(this, "queueTime", void 0);
        _define_property(this, "startTime", void 0);
        _define_property(this, "duration", void 0);
        _define_property(this, "target", void 0);
        _define_property(this, "threadId", void 0);
        this.options = options;
        _class_private_field_set(this, _status, "pending");
        this.queueTime = [
            0,
            0
        ];
        this.startTime = [
            0,
            0
        ];
        this.duration = [
            0,
            0
        ];
        this.threadId = 0;
        this.target = options.target;
        if (this.target.id === (0, _targetgraph.getStartTargetId)()) {
            _class_private_field_set(this, _status, "success");
        }
        this.options.logger.info("", {
            target: this.target,
            status: this.status
        });
    }
}
